import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state, query } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { marked } from "marked";
import DOMPurify from "dompurify";

/**
 * <farmazucca-chat> — il chatbot del sito farmaciazucca.it.
 *
 * Parla con il webhook n8n `farmazucca/webchat` (canale `web`): stessa AI e
 * stessi tool di WhatsApp/Instagram/voce. L'identità è la sessione anonima del
 * browser (localStorage): niente telefono — lo chiede l'AI quando serve, e i
 * link di conferma viaggiano via SMS, che ne prova la titolarità.
 *
 * Palette dal sito: verde #77B945, blu #004164, azzurro #1bb0ce.
 */

interface Msg {
  id: string;
  role: "user" | "assistant";
  da?: string; // user | ai | operator
  testo: string;
  quando?: string;
  locale?: boolean; // solo a schermo (saluto), non viene dal server
}

const DEFAULT_API =
  "https://api-farmazucca.aiknowyou.ai/webhook/farmazucca/webchat";
// «assistente virtuale» dichiarato nel saluto, prima ancora che il cliente scriva:
// è la trasparenza chiesta dall'AI Act (art. 50) — sui canali senza saluto (WA/IG/FB)
// la stessa presentazione la antepone l'engine alla prima risposta.
const GREETING =
  "Buongiorno! Sono il dottor Francesco, l'assistente virtuale della Farmacia Zucca: posso darle informazioni su servizi, prezzi e orari, e aiutarla a prenotare. Come posso aiutarla?";
const SESSION_KEY = "fz_chat_session";
// La finestra riaperta com'era: se il cliente naviga con la chat aperta, sulla pagina
// nuova la ritrova aperta e con la sua storia — la conversazione sopravvive al sito.
const OPEN_KEY = "fz_chat_open";
const POLL_MS = 5000;

function nuovoSid(): string {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  const sid = "ws_" + Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  localStorage.setItem(SESSION_KEY, sid);
  return sid;
}

function sessionId(): string {
  const sid = localStorage.getItem(SESSION_KEY);
  return sid && /^ws_[a-f0-9]{16,64}$/.test(sid) ? sid : nuovoSid();
}

function renderMd(testo: string): string {
  const raw = marked.parse(testo, { async: false, breaks: true }) as string;
  return DOMPurify.sanitize(raw, {
    ALLOWED_TAGS: ["p", "br", "strong", "em", "ul", "ol", "li", "a", "code"],
    ALLOWED_ATTR: ["href", "target", "rel"],
  });
}

@customElement("farmazucca-chat")
export class FarmazuccaChat extends LitElement {
  @property({ attribute: "api-url" }) apiUrl = DEFAULT_API;

  @state() private open = false;
  @state() private messages: Msg[] = [];
  @state() private typing = false;
  @state() private handoff = false;
  @state() private errore = "";
  @state() private draft = "";
  @state() private confermaNuova = false;

  @query(".msgs") private msgsEl?: HTMLElement;
  @query("textarea") private inputEl?: HTMLTextAreaElement;

  private sid = sessionId();
  private lastTs: string | null = null;
  private pollTimer: number | null = null;

  static styles = css`
    :host {
      position: fixed;
      /* Sotto il blocco delle icone social del tema (che è centrato in verticale) ma
         staccato da freccetta "torna su" e badge cookie che affollano l'angolo in
         basso (richiesta di Natalia, call del 04/08). Sovrascrivibile dal sito con
         --fz-chat-bottom senza ripubblicare il widget. */
      bottom: var(--fz-chat-bottom, 6.5rem);
      /* 0.4rem: il cerchio (3.5rem) risulta centrato sulla colonna delle icone
         social del tema (larga ~67px e attaccata al bordo). */
      right: var(--fz-chat-right, 0.4rem);
      z-index: 99999;
      --fz-verde: #77b945;
      --fz-verde-scuro: #5e9b32;
      --fz-blu: #004164;
      --fz-azzurro: #1bb0ce;
      --fz-fondo: #f6f8f6;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
        "Helvetica Neue", Arial, sans-serif;
      font-size: 15px;
      line-height: 1.45;
      color: #1e293b;
    }
    * { box-sizing: border-box; }
    /* Su mobile il blocco social del tema sparisce: basta scavalcare freccetta e
       badge cookie, senza lasciare il bottone a mezz'aria. */
    @media (max-width: 640px) {
      :host { bottom: var(--fz-chat-bottom-mobile, 5.5rem); right: 1rem; }
    }

    .toggle {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 3.5rem;
      height: 3.5rem;
      margin-left: auto;
      border: none;
      border-radius: 50%;
      background: var(--fz-verde);
      color: #fff;
      cursor: pointer;
      box-shadow: 0 6px 20px rgba(0, 65, 100, 0.28);
      transition: transform 0.15s ease, background 0.15s ease;
    }
    .toggle:hover { background: var(--fz-verde-scuro); transform: scale(1.05); }
    .toggle svg { width: 2.2rem; height: 2.2rem; }
    /* I tre puntini "sta scrivendo" dentro la nuvoletta: un invito alla chat che si
       muove da solo e cattura l'occhio (idea di Natalia, call del 04/08). */
    .toggle .dot { animation: fz-dot 1.2s ease-in-out infinite; }
    .toggle .dot:nth-child(3) { animation-delay: 0.15s; }
    .toggle .dot:nth-child(4) { animation-delay: 0.3s; }
    @keyframes fz-dot {
      0%, 60%, 100% { transform: translateY(0); opacity: 0.75; }
      30% { transform: translateY(-2.2px); opacity: 1; }
    }
    @media (prefers-reduced-motion: reduce) {
      .toggle .dot { animation: none; }
    }

    /* Pannello laterale a tutta altezza (03/08, richiesta di Giuseppe): più largo e
       più leggibile della finestrella flottante. Su mobile prende tutto lo schermo. */
    .window {
      position: fixed;
      top: 0;
      right: 0;
      display: flex;
      flex-direction: column;
      width: min(490px, 100vw);
      height: 100dvh;
      overflow: hidden;
      background: #fff;
      box-shadow: -12px 0 48px rgba(0, 65, 100, 0.28);
      animation: fz-slide 0.22s ease-out;
    }
    @media (min-width: 480px) {
      .window { border-radius: 1rem 0 0 1rem; }
    }
    @keyframes fz-slide {
      from { transform: translateX(40px); opacity: 0.4; }
      to { transform: translateX(0); opacity: 1; }
    }

    .header {
      display: flex;
      align-items: center;
      gap: 0.7rem;
      padding: 1rem 1.1rem;
      background: var(--fz-blu);
      color: #fff;
    }
    .header .logo {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 2.2rem;
      height: 2.2rem;
      border-radius: 50%;
      background: var(--fz-verde);
      flex-shrink: 0;
    }
    .header .logo svg { width: 1.3rem; height: 1.3rem; }
    .header .titolo { flex: 1; min-width: 0; }
    .header .titolo b { display: block; font-size: 0.95rem; }
    .header .titolo span { display: block; font-size: 0.75rem; opacity: 0.75; }
    .header button {
      border: none;
      background: none;
      color: #fff;
      opacity: 0.75;
      cursor: pointer;
      padding: 0.3rem;
    }
    .header button:hover { opacity: 1; }
    .header svg { width: 1.15rem; height: 1.15rem; display: block; }

    .msgs {
      flex: 1;
      overflow-y: auto;
      padding: 1.1rem 1.1rem 1.3rem;
      display: flex;
      flex-direction: column;
      gap: 0.7rem;
      background: var(--fz-fondo);
      font-size: 0.95rem;
      line-height: 1.55;
    }
    .row { display: flex; }
    .row.user { justify-content: flex-end; }
    /* Il limite di larghezza sta sul CONTENITORE, non solo sulla bolla: una percentuale
       sulla bolla dentro un wrapper shrink-to-fit collassava i messaggi corti su più
       righe («eh ¶ mandamelo ¶ ok»). */
    .row > div { max-width: 86%; min-width: 0; }
    .bubble {
      max-width: 100%;
      width: fit-content;
      padding: 0.6rem 0.9rem;
      border-radius: 0.95rem;
      overflow-wrap: break-word;
    }
    .row.user .bubble { margin-left: auto; }
    .row.user .bubble {
      background: var(--fz-verde);
      color: #fff;
      border-bottom-right-radius: 0.25rem;
    }
    .row.assistant .bubble {
      background: #fff;
      border: 1px solid #e2e8f0;
      border-bottom-left-radius: 0.25rem;
    }
    .bubble p { margin: 0 0 0.4rem; }
    .bubble p:last-child { margin: 0; }
    .bubble ul, .bubble ol { margin: 0.2rem 0; padding-left: 1.2rem; }
    .bubble a { color: var(--fz-azzurro); word-break: break-all; }
    .row.user .bubble a { color: #eaffdd; }
    .operatore {
      font-size: 0.7rem;
      color: var(--fz-blu);
      font-weight: 600;
      margin: 0 0 0.15rem 0.2rem;
    }

    .typing {
      display: inline-flex;
      gap: 0.25rem;
      padding: 0.7rem 0.9rem;
      background: #fff;
      border: 1px solid #e2e8f0;
      border-radius: 0.9rem;
      border-bottom-left-radius: 0.25rem;
    }
    .typing i {
      width: 0.45rem;
      height: 0.45rem;
      border-radius: 50%;
      background: #94a3b8;
      animation: fz-b 1.2s infinite;
    }
    .typing i:nth-child(2) { animation-delay: 0.15s; }
    .typing i:nth-child(3) { animation-delay: 0.3s; }
    @keyframes fz-b {
      0%, 60%, 100% { transform: translateY(0); opacity: 0.5; }
      30% { transform: translateY(-4px); opacity: 1; }
    }

    .conferma-nuova {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-wrap: wrap;
      padding: 0.55rem 1rem;
      background: #eef7e8;
      border-bottom: 1px solid #d6ecc6;
      font-size: 0.78rem;
      color: #2f5215;
    }
    .conferma-nuova span { flex: 1 1 100%; }
    .conferma-nuova button {
      border: none;
      border-radius: 999px;
      padding: 0.3rem 0.8rem;
      font: inherit;
      font-weight: 600;
      cursor: pointer;
    }
    .conferma-nuova .si { background: var(--fz-verde); color: #fff; }
    .conferma-nuova .si:hover { background: var(--fz-verde-scuro); }
    .conferma-nuova .no { background: #fff; color: #475569; border: 1px solid #cbd5e1; }

    .nota {
      align-self: center;
      max-width: 90%;
      padding: 0.4rem 0.8rem;
      border-radius: 0.6rem;
      background: #e8f2fa;
      color: var(--fz-blu);
      font-size: 0.78rem;
      text-align: center;
    }
    .err {
      align-self: center;
      color: #b91c1c;
      background: #fee2e2;
      font-size: 0.78rem;
      padding: 0.4rem 0.8rem;
      border-radius: 0.6rem;
    }

    .inputbar {
      display: flex;
      align-items: flex-end;
      gap: 0.5rem;
      padding: 0.7rem;
      background: #fff;
      border-top: 1px solid #e2e8f0;
    }
    textarea {
      flex: 1;
      resize: none;
      border: 1px solid #cbd5e1;
      border-radius: 0.7rem;
      padding: 0.55rem 0.75rem;
      font: inherit;
      max-height: 6.5rem;
      outline: none;
    }
    textarea:focus { border-color: var(--fz-verde); }
    .send {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 2.6rem;
      height: 2.6rem;
      border: none;
      border-radius: 50%;
      background: var(--fz-verde);
      color: #fff;
      cursor: pointer;
      flex-shrink: 0;
    }
    .send:disabled { opacity: 0.45; cursor: default; }
    .send svg { width: 1.2rem; height: 1.2rem; }

    .footer {
      padding: 0.35rem 0.7rem 0.55rem;
      background: #fff;
      font-size: 0.68rem;
      color: #94a3b8;
      text-align: center;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  `;

  // I bottoni <farmazucca-chat-trigger> sparsi nel sito parlano con la chat via
  // evento su window: al click la chat si apre e il messaggio parte come se
  // l'avesse scritto il visitatore.
  private onTrigger = (e: Event) => {
    const msg = String((e as CustomEvent).detail?.message || "").trim();
    if (msg) void this.inviaDaTrigger(msg);
  };

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("farmazucca-chat:send", this.onTrigger);
    // Se sulla pagina precedente la chat era aperta, si riapre da sola.
    if (localStorage.getItem(OPEN_KEY) === "1") {
      this.open = true;
      this.startPoll();
    }
    void this.caricaStoria();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("farmazucca-chat:send", this.onTrigger);
    this.stopPoll();
  }

  private async inviaDaTrigger(msg: string) {
    if (!this.open) {
      this.open = true;
      localStorage.setItem(OPEN_KEY, "1");
      this.startPoll();
      await this.updateComplete;
    }
    // se una risposta è già in corso il click si ignora: doppio click, doppio bottone
    if (this.typing) return;
    await this.inviaTesto(msg);
  }

  private startPoll() {
    if (this.pollTimer != null) return;
    this.pollTimer = window.setInterval(() => void this.poll(), POLL_MS);
  }
  private stopPoll() {
    if (this.pollTimer != null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private async api(body: Record<string, unknown>): Promise<any> {
    const res = await fetch(this.apiUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: this.sid, ...body }),
    });
    return res.json();
  }

  private aggiungi(msgs: Msg[]) {
    const noti = new Set(this.messages.map((m) => m.id));
    let lista = this.messages;
    let cambiato = false;
    for (const m of msgs) {
      if (noti.has(m.id)) continue;
      if (m.quando && (!this.lastTs || m.quando > this.lastTs)) this.lastTs = m.quando;
      // La copia server di una bolla mostrata in locale (id ai_/loc_) non è un messaggio
      // nuovo: la bolla ADOTTA l'id vero, altrimenti il polling la raddoppierebbe.
      const locIdx = lista.findIndex((x) => x.locale && x.role === m.role && x.testo === m.testo);
      if (locIdx >= 0) {
        lista = lista.map((x, i) => (i === locIdx ? { ...x, id: m.id, quando: m.quando, locale: false } : x));
        noti.add(m.id);
        cambiato = true;
        continue;
      }
      lista = [...lista, m];
      noti.add(m.id);
      cambiato = true;
    }
    if (!cambiato) return;
    this.messages = lista;
    void this.updateComplete.then(() => this.scrollGiu());
  }

  private async caricaStoria() {
    try {
      const out = await this.api({ azione: "history" });
      if (out?.success) {
        if (out.ai_enabled === false) this.handoff = true;
        const msgs: Msg[] = (out.messages || []).map((m: any) => ({
          id: m.id, role: m.role, da: m.da, testo: m.testo, quando: m.quando,
        }));
        if (!msgs.length)
          msgs.push({ id: "greeting", role: "assistant", da: "ai", testo: GREETING, locale: true });
        this.aggiungi(msgs);
      }
    } catch {
      /* la storia si riprova alla prossima apertura */
    }
  }

  /** Polling: raccoglie le risposte scritte dall'operatore dalla platform. */
  private async poll() {
    if (!this.open || this.typing) return;
    try {
      const out = await this.api({ azione: "history", after: this.lastTs });
      if (out?.success) {
        this.handoff = out.ai_enabled === false;
        this.aggiungi(
          (out.messages || [])
            .filter((m: any) => m.role === "assistant")
            .map((m: any) => ({ id: m.id, role: m.role, da: m.da, testo: m.testo, quando: m.quando })),
        );
      }
    } catch {
      /* il prossimo giro riprova */
    }
  }

  private async invia() {
    const testo = this.draft.trim();
    if (!testo || this.typing) return;
    this.draft = "";
    if (this.inputEl) this.inputEl.style.height = "auto";
    await this.inviaTesto(testo);
  }

  /** Il percorso d'invio vero, usato dall'input e dai trigger del sito. */
  private async inviaTesto(testo: string) {
    this.errore = "";
    this.aggiungi([{ id: "loc_" + Date.now(), role: "user", da: "user", testo, locale: true }]);
    this.typing = true;
    try {
      const out = await this.api({ azione: "send", message: testo, pagina: location.href });
      if (out?.success && out.reply) {
        this.aggiungi([{ id: "ai_" + Date.now(), role: "assistant", da: "ai", testo: out.reply, locale: true }]);
      } else if (out?.success && out.handoff) {
        this.handoff = true;
      } else if (out?.error === "troppi_messaggi") {
        this.errore = "Troppi messaggi di fila: attenda un momento e riprovi.";
      } else if (!out?.success) {
        this.errore = "Non sono riuscito a inviare il messaggio: riprovi tra poco.";
      }
      if (out?.reply === null && out?.nota) this.handoff = true;
    } catch {
      this.errore = "Connessione non riuscita: controlli la rete e riprovi.";
    } finally {
      this.typing = false;
      void this.updateComplete.then(() => {
        this.scrollGiu();
        this.inputEl?.focus();
      });
    }
  }

  private scrollGiu() {
    this.msgsEl?.scrollTo({ top: this.msgsEl.scrollHeight });
  }

  private toggleOpen() {
    this.open = !this.open;
    localStorage.setItem(OPEN_KEY, this.open ? "1" : "0");
    if (this.open) {
      this.startPoll();
      void this.updateComplete.then(() => {
        this.scrollGiu();
        this.inputEl?.focus();
      });
    } else this.stopPoll();
  }

  /** Nuova conversazione: sessione nuova (la vecchia resta in farmacia), chat pulita. */
  private nuovaConversazione() {
    this.confermaNuova = false;
    this.sid = nuovoSid();
    this.lastTs = null;
    this.handoff = false;
    this.errore = "";
    this.messages = [{ id: "greeting", role: "assistant", da: "ai", testo: GREETING, locale: true }];
    void this.updateComplete.then(() => this.inputEl?.focus());
  }

  private onKey(e: KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void this.invia();
    }
  }
  private onInput(e: Event) {
    const el = e.target as HTMLTextAreaElement;
    this.draft = el.value;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 104) + "px";
  }

  render() {
    return html`
      <div>
        ${this.open
          ? html`
              <div class="window" role="dialog" aria-label="Chat Farmacia Zucca">
                <div class="header">
                  <span class="logo" aria-hidden="true">${iconCroce}</span>
                  <span class="titolo">
                    <b>Farmacia Zucca</b>
                    <span>${this.handoff ? "Un operatore la sta seguendo" : "Assistente virtuale"}</span>
                  </span>
                  <button
                    @click=${() => (this.confermaNuova = !this.confermaNuova)}
                    title="Nuova conversazione"
                    aria-label="Inizia una nuova conversazione"
                  >
                    ${iconNuova}
                  </button>
                  <button @click=${this.toggleOpen} title="Chiudi" aria-label="Chiudi la chat">
                    ${iconChiudi}
                  </button>
                </div>

                ${this.confermaNuova
                  ? html`
                      <div class="conferma-nuova">
                        <span>Iniziare una nuova conversazione? Quella attuale resta comunque alla farmacia.</span>
                        <button class="si" @click=${this.nuovaConversazione}>Sì, ricomincia</button>
                        <button class="no" @click=${() => (this.confermaNuova = false)}>Annulla</button>
                      </div>
                    `
                  : nothing}

                <div class="msgs">
                  ${this.messages.map(
                    (m) => html`
                      <div class="row ${m.role}">
                        <div>
                          ${m.da === "operator"
                            ? html`<div class="operatore">Farmacia Zucca · operatore</div>`
                            : nothing}
                          <div class="bubble">${unsafeHTML(renderMd(m.testo))}</div>
                        </div>
                      </div>
                    `,
                  )}
                  ${this.typing
                    ? html`<div class="row assistant"><div class="typing"><i></i><i></i><i></i></div></div>`
                    : nothing}
                  ${this.handoff
                    ? html`<div class="nota">Un operatore della farmacia sta seguendo la conversazione: le risposte arrivano qui.</div>`
                    : nothing}
                  ${this.errore ? html`<div class="err">${this.errore}</div>` : nothing}
                </div>

                <div class="inputbar">
                  <textarea
                    rows="1"
                    placeholder="Scriva il suo messaggio…"
                    .value=${this.draft}
                    @input=${this.onInput}
                    @keydown=${this.onKey}
                    aria-label="Messaggio"
                  ></textarea>
                  <button
                    class="send"
                    @click=${this.invia}
                    ?disabled=${!this.draft.trim() || this.typing}
                    title="Invia"
                    aria-label="Invia"
                  >
                    ${iconInvia}
                  </button>
                </div>
                <div class="footer">Assistente AI · conversazioni visibili al personale della farmacia</div>
              </div>
            `
          : nothing}
        ${this.open
          ? nothing
          : html`
              <button
                class="toggle"
                @click=${this.toggleOpen}
                title="Chatta con la Farmacia Zucca"
                aria-label="Apri la chat della Farmacia Zucca"
              >
                ${iconChat}
              </button>
            `}
      </div>
    `;
  }
}

const iconChat = html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/><circle class="dot" cx="8" cy="11.5" r="1.15" fill="currentColor" stroke="none"/><circle class="dot" cx="12" cy="11.5" r="1.15" fill="currentColor" stroke="none"/><circle class="dot" cx="16" cy="11.5" r="1.15" fill="currentColor" stroke="none"/></svg>`;
const iconChiudi = html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>`;
const iconNuova = html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14M5 12h14"/></svg>`;
const iconInvia = html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m22 2-7 20-4-9-9-4Z"/><path d="M22 2 11 13"/></svg>`;
const iconCroce = html`<svg viewBox="0 0 24 24" fill="currentColor"><path d="M10 3h4v7h7v4h-7v7h-4v-7H3v-4h7z"/></svg>`;

declare global {
  interface HTMLElementTagNameMap {
    "farmazucca-chat": FarmazuccaChat;
  }
}
